import { Helmet } from "react-helmet-async";

const SITE_URL = "https://onestep.co.il";
const DEFAULT_IMAGE = "https://onestep.co.il/images/hero-dashboard.webp";

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOProps {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  image?: string;
  noindex?: boolean;
  type?: "website" | "article";
  publishedTime?: string;
  author?: string;
  breadcrumbs?: BreadcrumbItem[];
  ogTitle?: string;
  ogDescription?: string;
}

const SEO = ({ title, description, path, jsonLd, image = DEFAULT_IMAGE, noindex, type = "website", publishedTime, author, breadcrumbs, ogTitle, ogDescription }: SEOProps) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = path === "/" ? title : `${title} | OneStep`;

  // Ensure image is always absolute https URL
  const absoluteImage = image.startsWith('http') ? image : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`;

  // Use ogTitle/ogDescription if provided, otherwise fall back to defaults
  const effectiveOgTitle = ogTitle || fullTitle;
  const effectiveOgDescription = ogDescription || description;

  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  } : null;

  const allJsonLd = breadcrumbSchema
    ? Array.isArray(jsonLd)
      ? [...jsonLd, breadcrumbSchema]
      : jsonLd
        ? [jsonLd, breadcrumbSchema]
        : breadcrumbSchema
    : jsonLd;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* hreflang for Israeli Hebrew audience */}
      <link rel="alternate" hrefLang="he-IL" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={effectiveOgTitle} />
      <meta property="og:description" content={effectiveOgDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="he_IL" />
      <meta property="og:site_name" content="OneStep" />

      {/* Article-specific OG tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={effectiveOgTitle} />
      <meta name="twitter:description" content={effectiveOgDescription} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Geo tags for Israel */}
      <meta name="geo.region" content="IL" />
      <meta name="geo.placename" content="Israel" />

      {/* JSON-LD */}
      {allJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(allJsonLd) ? allJsonLd : allJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
